// components/PurchaseOrderPDF.jsx
import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import moment from "moment";

const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    fontSize: 9.5,
    padding: 15,
    lineHeight: 1.2,
    color: "#000",
  },
  columnBox: {
    width: "33.33%", // 3 in a row
    padding: 2,
  },
  title: {
    fontSize: 15,
    textAlign: "center",
    color: "#0b57d0",
    fontWeight: "bold",
    marginBottom: 4,
  },
  section: {
    marginBottom: 4,
  },
  bold: {
    fontWeight: "bold",
  },
  headerLine: {
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
    borderBottomStyle: "solid",
    marginBottom: 4,
    paddingBottom: 2,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 5,
  },
  column: {
    flex: 1,
    paddingRight: 4,
  },
  smallText: {
    fontSize: 9.5,
  },
  link: {
    color: "#0b57d0",
    textDecoration: "none",
  },
});

const twoDecimal = (x) => {
  const num = Number.parseFloat(x).toFixed(2);
  const [integerPart, decimalPart] = num.split(".");
  const formattedInteger = integerPart.replace(/(\d)(?=(\d\d)+\d$)/g, "$1,");
  return `${formattedInteger}.${decimalPart}`;
};

function roundUpToInteger(value) {
  const roundedValue = Number.parseFloat(value);
  const num = roundedValue.toFixed(2);
  const [integerPart, decimalPart] = num.split(".");
  const formattedInteger = integerPart.replace(/(\d)(?=(\d\d)+\d$)/g, "$1,");
  return `${formattedInteger}.${decimalPart}`;
}

const PDFDocument = ({
  poDetails,
  paymentList,
  poid,
  projectDetails,
  ClassificationData,
  companyLogo,
}) => {
  function stripHtml(html) {
    if (!html) return "";
    return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  }
  const poNo = poDetails?.po_no || "";
  const match = poNo.match(/T(\d+)\//);
  const extractedNumber = match ? match[1] : "";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Title */}
        <View
          style={{
            backgroundColor: "#0056b3",
            paddingVertical: 4,
            paddingHorizontal: 8,
            width: "100%",
            marginBottom: 8,
          }}
        >
          <Text
            style={{
              color: "white",
              fontSize: 15,
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            {poDetails.order_type == "1" ? "Purchase Order" : "Work Order"}
          </Text>
        </View>

        {/* PO Number */}
        <View
          style={{
            flexDirection: "row",
            justifyContent: "flex-end",
            borderBottomWidth: 1,
            borderBottomColor: "#000",
            marginTop: 4,
            marginBottom: 6,
          }}
        >
          <Text style={{ fontWeight: "bold", fontSize: 10 }}>
            {extractedNumber} {poDetails.vendor_name}{" "}
            {moment(poDetails.po_date).format("DD-MM-YYYY")} @
            {poDetails.project_name}
          </Text>
        </View>

        {/* Company Info and Logo Row */}
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-start",
            borderBottomWidth: 1,
            borderBottomColor: "#000",
            marginTop: 4,
            marginBottom: 6,
          }}
        >
          {/* Left: Logo */}
          <View
            style={{
              width: "30%",
              alignItems: "center",
              justifyContent: "center",
              marginTop: 4,
            }}
          >
            <Image
              src={companyLogo}
              style={{
                width: 90,
                height: 35,
                objectFit: "contain",
              }}
            />
          </View>

          {/* Right: Company Info */}
          <View style={{ width: "70%", alignItems: "flex-end" }}>
            <Text
              style={{
                fontWeight: "bold",
                fontSize: 11,
                marginTop: 2,
                color: "#0056b3",
              }}
            >
              {poDetails.company_Name}
            </Text>

            <Text style={{ fontSize: 9.5, marginTop: 2 }}>
              {poDetails
                ? poDetails.company_address?.trim().replace(/\s+/g, " ")
                : `Plot No 108, Perumal Koil Street, Alapakkam Porur`}
            </Text>

            <Text style={{ fontSize: 9.5, marginTop: 2 }}>
              {poDetails
                ? `${poDetails.company_city} - ${poDetails.company_pincode}`
                : `Chennai - 600116`}
            </Text>

            <Text
              style={{
                fontSize: 9.5,
                marginTop: 2,
                color: "blue",
                textDecoration: "underline",
              }}
            >
              {poDetails
                ? poDetails.company_website
                : `https://kgbuildtech.com`}
            </Text>

            <Text style={{ fontSize: 9.5, marginTop: 2 }}>
              {poDetails ? `GST NO: ${poDetails.gst_no}` : ``}
            </Text>
          </View>
        </View>

        {/* 3 Column Metadata Info */}
        <View style={{ flexDirection: "row", flexWrap: "wrap", marginTop: 4 }}>
          <View style={styles.columnBox}>
            <Text>
              <Text style={{ fontWeight: "bold" }}>
                {poDetails.order_type == "1" ? "PO Order No: " : "WO Order No: "}
              </Text>
              {poDetails.po_no}
            </Text>
          </View>
          <View style={styles.columnBox}>
            <Text>
              <Text style={{ fontWeight: "bold" }}>
                {poDetails.order_type == "1" ? "PO Date: " : "WO Date: "}
              </Text>
              {moment(poDetails.po_date).format("DD-MM-YYYY")}
            </Text>
          </View>
          <View style={styles.columnBox}>
            <Text>
              <Text style={{ fontWeight: "bold" }}>Supplier Ref: </Text>
              {poDetails.quotation_no}
            </Text>
          </View>

          <View style={styles.columnBox}>
            <Text>
              <Text style={{ fontWeight: "bold" }}>
                {poDetails.order_type == "1"
                  ? "PO Classification: "
                  : "WO Classification: "}
              </Text>
              {poDetails.clasification_name}
            </Text>
          </View>
          <View style={styles.columnBox}>
            <Text>
              Site Engineer:{" "}
              <Text style={{ color: "#0056b3", fontWeight: "bold" }}>
                {poDetails.site_eng_name}
              </Text>
            </Text>
          </View>
          <View style={styles.columnBox}>
            <Text>
              Contact:{" "}
              <Text style={{ color: "#0056b3", fontWeight: "bold" }}>
                {poDetails.site_eng_contact}
              </Text>
            </Text>
          </View>
        </View>

        {/* Address Row */}
        <View style={[styles.row, { marginTop: 6, borderBottomWidth: 1, borderBottomColor: "#ccc", paddingBottom: 6 }]}>
          <View style={styles.column}>
            <Text style={{ fontWeight: "bold", fontSize: 10 }}>Bill To :</Text>
            <Text
              style={{
                fontSize: 10.5,
                color: "#0056b3",
                fontWeight: "bold",
                marginTop: 2,
              }}
            >
              {poDetails.vendor_name}
            </Text>
            <Text style={{ marginTop: 1, fontSize: 9.5 }}>{poDetails.vendor_mobile}</Text>
            <Text style={{ marginTop: 1, fontSize: 9.5 }}>{poDetails.vendor_street}</Text>
            <Text style={{ marginTop: 1, fontSize: 9.5 }}>{poDetails.vendor_city}</Text>
            {poDetails.vendor_state?.toLowerCase() !== "tamilnadu" && (
              <Text style={{ marginTop: 1, fontSize: 9.5 }}>{poDetails.vendor_state}, India</Text>
            )}
            <Text style={{ marginTop: 1, fontSize: 9.5 }}>{poDetails.vendor_pincode}</Text>
            <Text style={{ marginTop: 1, fontWeight: "bold", fontSize: 9.5 }}>GSTIN : {poDetails.vendor_gst}</Text>
          </View>

          <View style={styles.column}>
            <Text style={{ fontWeight: "bold", fontSize: 10 }}>Place of Supply :</Text>
            <Text
              style={{
                fontSize: 10.5,
                color: "#0056b3",
                fontWeight: "bold",
                marginTop: 2,
              }}
            >
              {projectDetails.name}
            </Text>
            <Text style={{ marginTop: 1, fontSize: 9.5 }}>{projectDetails.project_address}</Text>
            <Text style={{ marginTop: 1, fontSize: 9.5 }}>{projectDetails.city}</Text>
            {projectDetails.state?.toLowerCase() !== "tamilnadu" && (
              <Text style={{ marginTop: 1, fontSize: 9.5 }}>{projectDetails.state}, India</Text>
            )}
            <Text style={{ marginTop: 1, fontSize: 9.5 }}>{projectDetails.pincode}</Text>
          </View>
        </View>

        {/* Table */}
        <View style={{ marginTop: 6 }}>
          <View
            style={{
              flexDirection: "row",
              borderTop: "1 solid black",
              borderBottom: "1 solid black",
              backgroundColor: "#f0f0f0",
              fontWeight: "bold",
            }}
          >
            <Text style={{ width: "7%", padding: 3, fontSize: 7.5, textAlign: "center", borderRight: "1 solid black", borderLeft: "1 solid black" }}>
              S.No
            </Text>
            <Text style={{ width: "40%", padding: 3, fontSize: 7.5, textAlign: "center", borderRight: "1 solid black" }}>
              Description of Goods
            </Text>
            <Text style={{ width: "8%", padding: 3, fontSize: 7.5, textAlign: "center", borderRight: "1 solid black" }}>
              Qty
            </Text>
            <Text style={{ width: "10%", padding: 3, fontSize: 7.5, textAlign: "center", borderRight: "1 solid black" }}>
              Amt(Rs)
            </Text>
            <Text style={{ width: "10%", padding: 3, fontSize: 7.5, textAlign: "center", borderRight: "1 solid black" }}>
              Discount
            </Text>
            <Text style={{ width: "10%", padding: 3, fontSize: 7.5, textAlign: "center", borderRight: "1 solid black" }}>
              GST
            </Text>
            <Text style={{ width: "15%", padding: 3, fontSize: 7.5, textAlign: "center", borderRight: "1 solid black" }}>
              Total Amt
            </Text>
          </View>

          {paymentList &&
            paymentList.map((item, index) => (
              <View
                key={index}
                wrap={false}
                style={{
                  flexDirection: "row",
                  borderBottom: "1 solid black",
                  fontWeight: "normal",
                }}
              >
                <Text style={{ width: "7%", padding: 3, fontSize: 7.5, textAlign: "center", borderRight: "1 solid black", borderLeft: "1 solid black" }}>
                  {index + 1}
                </Text>
                <Text style={{ width: "40%", padding: 3, fontSize: 7.5, textAlign: "left", borderRight: "1 solid black" }}>
                  {item.item_name}
                </Text>
                <Text style={{ width: "8%", padding: 3, fontSize: 7.5, textAlign: "center", borderRight: "1 solid black" }}>
                  {twoDecimal(item.quantity)} {"\n"}({item.unit_name})
                </Text>
                <Text style={{ width: "10%", padding: 3, fontSize: 7.5, textAlign: "center", borderRight: "1 solid black" }}>
                  {twoDecimal(item.amount)}
                </Text>
                <Text style={{ width: "10%", padding: 3, fontSize: 7.5, textAlign: "center", borderRight: "1 solid black" }}>
                  {item.discount_type === "fixed"
                    ? `Rs. ${twoDecimal(item.discount_value)}`
                    : item.discount_type === "percentage"
                      ? `${item.discount_value}%`
                      : "—"}
                </Text>
                <Text style={{ width: "10%", padding: 3, fontSize: 7.5, textAlign: "center", borderRight: "1 solid black" }}>
                  {item.gst}%
                </Text>
                <Text style={{ width: "15%", padding: 3, fontSize: 7.5, textAlign: "center", borderRight: "1 solid black" }}>
                  {twoDecimal(
                    item.discount_type === "fixed"
                      ? item.amount * item.quantity - item.discount_value
                      : item.discount_type === "percentage"
                        ? item.amount * item.quantity * (1 - item.discount_value / 100)
                        : item.amount * item.quantity
                  )}
                </Text>
              </View>
            ))}

          {/* Total row 1 */}
          <View wrap={false} style={{ flexDirection: "row" }}>
            <Text style={{ width: "7%", borderRight: "1 solid black", borderLeft: "1 solid black" }}></Text>
            <Text style={{ width: "40%", borderRight: "1 solid black" }}></Text>
            <Text style={{ width: "8%", borderRight: "1 solid black" }}></Text>
            <Text style={{ width: "30%", padding: 3, fontSize: 7.5, textAlign: "right", borderRight: "1 solid black", borderBottom: "1 solid black", fontWeight: "bold" }}>
              Total amount without GST
            </Text>
            <Text style={{ width: "15%", padding: 3, fontSize: 7.5, textAlign: "right", borderRight: "1 solid black", borderBottom: "1 solid black", fontWeight: "bold" }}>
              {twoDecimal(poDetails.total_without_gst)}
            </Text>
          </View>

          {/* IGST / CGST / SGST */}
          {poDetails.total_igst > 0 ? (
            <View wrap={false} style={{ flexDirection: "row" }}>
              <Text style={{ width: "7%", borderRight: "1 solid black", borderLeft: "1 solid black" }}></Text>
              <Text style={{ width: "40%", borderRight: "1 solid black" }}></Text>
              <Text style={{ width: "8%", borderRight: "1 solid black" }}></Text>
              <Text style={{ width: "30%", padding: 3, fontSize: 7.5, textAlign: "right", borderRight: "1 solid black", borderBottom: "1 solid black" }}>
                IGST
              </Text>
              <Text style={{ width: "15%", padding: 3, fontSize: 7.5, textAlign: "right", borderRight: "1 solid black", borderBottom: "1 solid black" }}>
                {twoDecimal(poDetails.total_igst)}
              </Text>
            </View>
          ) : (
            <>
              <View wrap={false} style={{ flexDirection: "row" }}>
                <Text style={{ width: "7%", borderRight: "1 solid black", borderLeft: "1 solid black" }}></Text>
                <Text style={{ width: "40%", borderRight: "1 solid black" }}></Text>
                <Text style={{ width: "8%", borderRight: "1 solid black" }}></Text>
                <Text style={{ width: "30%", padding: 3, fontSize: 7.5, textAlign: "right", borderRight: "1 solid black", borderBottom: "1 solid black" }}>
                  CGST
                </Text>
                <Text style={{ width: "15%", padding: 3, fontSize: 7.5, textAlign: "right", borderRight: "1 solid black", borderBottom: "1 solid black" }}>
                  {twoDecimal(poDetails.total_cgst)}
                </Text>
              </View>
              <View wrap={false} style={{ flexDirection: "row" }}>
                <Text style={{ width: "7%", borderRight: "1 solid black", borderLeft: "1 solid black" }}></Text>
                <Text style={{ width: "40%", borderRight: "1 solid black" }}></Text>
                <Text style={{ width: "8%", borderRight: "1 solid black" }}></Text>
                <Text style={{ width: "30%", padding: 3, fontSize: 7.5, textAlign: "right", borderRight: "1 solid black", borderBottom: "1 solid black" }}>
                  SGST
                </Text>
                <Text style={{ width: "15%", padding: 3, fontSize: 7.5, textAlign: "right", borderRight: "1 solid black", borderBottom: "1 solid black" }}>
                  {twoDecimal(poDetails.total_sgst)}
                </Text>
              </View>
            </>
          )}

          {/* Transport */}
          <View wrap={false} style={{ flexDirection: "row" }}>
            <Text style={{ width: "7%", borderRight: "1 solid black", borderLeft: "1 solid black" }}></Text>
            <Text style={{ width: "40%", borderRight: "1 solid black" }}></Text>
            <Text style={{ width: "8%", borderRight: "1 solid black" }}></Text>
            <Text style={{ width: "30%", padding: 3, fontSize: 7.5, textAlign: "right", borderRight: "1 solid black", borderBottom: "1 solid black" }}>
              Transport Charges (GST {poDetails.transport_gst}%)
            </Text>
            <Text style={{ width: "15%", padding: 3, fontSize: 7.5, textAlign: "right", borderRight: "1 solid black", borderBottom: "1 solid black" }}>
              {twoDecimal(poDetails.transport_charge)}
            </Text>
          </View>

          {/* Payable Amount */}
          <View wrap={false} style={{ flexDirection: "row" }}>
            <Text style={{ width: "7%", borderRight: "1 solid black", borderLeft: "1 solid black", borderBottom: "1 solid black" }}></Text>
            <Text style={{ width: "40%", borderRight: "1 solid black", borderBottom: "1 solid black" }}></Text>
            <Text style={{ width: "8%", borderRight: "1 solid black", borderBottom: "1 solid black" }}></Text>
            <Text style={{ width: "30%", padding: 3, fontSize: 7.5, textAlign: "right", borderRight: "1 solid black", borderBottom: "1 solid black", color: "#0056b3", fontWeight: "bold" }}>
              Payable Amount
            </Text>
            <Text style={{ width: "15%", padding: 3, fontSize: 7.5, textAlign: "right", borderRight: "1 solid black", borderBottom: "1 solid black", fontWeight: "bold" }}>
              {twoDecimal(poDetails.total_with_gst)}
            </Text>
          </View>
        </View>

        {/* Terms & Conditions */}
        <View style={{ marginTop: 6, fontSize: 9.5, fontFamily: "Helvetica" }}>
          <View wrap={false} style={{ border: "1 solid black" }}>
            <Text style={{ backgroundColor: "#eceff1", textAlign: "center", fontWeight: "bold", padding: 4, fontSize: 10 }}>
              {poDetails.order_type == "1" ? "PO Terms & Condition(s):" : "WO Terms & Condition(s):"}
            </Text>
            <Text style={{ textAlign: "center", padding: 4, fontSize: 9.5, color: "#2f3e55", borderTop: "1 solid black" }}>
              {stripHtml(poDetails?.terms)}
            </Text>
          </View>

          {/* Bank Details */}
          <View wrap={false} style={{ marginTop: 8, padding: 6, border: "1 solid #2f3e55", borderRadius: 4, backgroundColor: "#f8f9fa" }}>
            <Text style={{ fontWeight: "bold", fontSize: 10, marginBottom: 4, color: "#2f3e55" }}>
              Bank Details
            </Text>
            <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
              <View style={{ flex: 1, marginRight: 5 }}>
                <Text style={{ marginBottom: 2 }}><Text style={{ fontWeight: "bold" }}>Bank Name: </Text>{poDetails.bank_name}</Text>
                <Text style={{ marginBottom: 2 }}><Text style={{ fontWeight: "bold" }}>Account Name: </Text>{poDetails.account_name}</Text>
                <Text style={{ marginBottom: 2 }}><Text style={{ fontWeight: "bold" }}>Account Number: </Text>{poDetails.account_no}</Text>
              </View>
              <View style={{ flex: 1, marginLeft: 5 }}>
                <Text style={{ marginBottom: 2 }}><Text style={{ fontWeight: "bold" }}>Branch: </Text>{poDetails.branch}</Text>
                <Text style={{ marginBottom: 2 }}><Text style={{ fontWeight: "bold" }}>IFSC Code: </Text>{poDetails.ifsc_code}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Signatures */}
        <View wrap={false} style={{ marginTop: 25, flexDirection: "row", justifyContent: "space-between" }}>
          <Text style={{ fontWeight: "bold", color: "#2f3e55", fontSize: 10 }}>Prepared By</Text>
          <Text style={{ fontWeight: "bold", color: "#2f3e55", fontSize: 10 }}>Verified By</Text>
          <Text style={{ fontWeight: "bold", color: "#2f3e55", fontSize: 10 }}>Approved By QS</Text>
          <Text style={{ fontWeight: "bold", color: "#2f3e55", fontSize: 10 }}>Approved By</Text>
          <Text style={{ fontWeight: "bold", color: "#2f3e55", fontSize: 10 }}>Contractor/Vendor Signature</Text>
        </View>
      </Page>
    </Document>
  );
};

export default PDFDocument;
